import React, { useContext, useEffect, useState, useRef } from 'react';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, ButtonGroup, Card, CardGroup, Col, Form, FormControl, InputGroup, ListGroup, OverlayTrigger, Pagination, Row, Spinner, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import { DATE_FORMAT } from '../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faDownload,
  faEdit,
  faEye,
  faEyeSlash,
  faInfoCircle,
  faPlusSquare,
  faPrint,
  faUserAlt
} from '@fortawesome/free-solid-svg-icons';
import {
  faDotCircle
} from '@fortawesome/free-regular-svg-icons';
import { useMark, useSubmitting } from '../hooks';
import Header from '../components/Header';
import { titleCase } from '../components/Utils';
import Scrollbar from '../components/Scrollbars';
import { ITEMS_PER_PAGE } from '../constants';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import { printAllOwnersDetails } from './Utils';
import LoadingLink from "../components/LoadingLink";
import ActionsDropdown from '../components/ActionsDropdown';

function PreRegOwnerSearch({ organization }) {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

	// Identify any query param data.
  const [queryParams, setQueryParams] = useQueryParams();
  const {
    search = '',
  } = queryParams;

	const [data, setData] = useState({owners: [], total_count: 0, isFetching: false});
	const [searchTerm, setSearchTerm] = useState(search);
  const [ownerType, setOwnerType] = useState('operational');
	const [isLoading, setIsLoading] = useState(false);
  const tempSearchTerm = useRef(null);
  const [showHidden, setShowHidden] = useState(false);
  // const [triggerRefresh, setTriggerRefresh] = useState(false);
	const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [goToID, setGoToID] = useState('');
  const {
    handleSubmitting,
    submittingComplete,
  } = useSubmitting();

  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter owners.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
		setPage(1);
  }

	function setFocus(pageNum) {
    if (pageNum !== page) {
      tempSearchTerm.current.focus();
    }
  }

  const showHideOwner = async (owner_id, bool) => {
    await axios.patch('/people/api/person/' + owner_id + '/', {'hidden':bool})
    .then(response => {
      setData(prevState => ({ ...prevState, "owners": data.owners.filter(owner => owner.id !== response.data.id) }));
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleGeoJsonDownload = (e) => {
    setIsLoading(true);

    handleSubmitting()
    .then(() => downloadGeoJson()
      )
    .then(submittingComplete)
    .finally(() => setIsLoading(false));
  }


  const downloadGeoJson = async () => {
    // Fetch all pre-reg person data.
    let pre_reg_persons = [];
    let nextUrl = '/people/api/pre-reg-person/?light=true&page=1&search=' + searchTerm + '&owner_type=' + ownerType + '&organization=' + organization + '&hidden=' + showHidden;
    do {
      const response = await axios.get(nextUrl)
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      pre_reg_persons.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/people/' + response.data.next.split('/people/')[1];
      }
    } while(nextUrl != null)

    var params = '';
    pre_reg_persons.forEach(person => params = params + "id=" + person.id + "&")
    var fileDownload = require('js-file-download');
    axios.get('/people/api/pre-reg-person/download_all/', { 
            params: {
              ids: params
            },
            responseType: 'blob',
        }).then(res => {
            fileDownload(res.data, `Persons-${moment().format(DATE_FORMAT)}` + '.geojson');
        }).catch(err => {
        })
  }

	// Hook for initializing data.
	useEffect(() => {
		let unmounted = false;
		let source = axios.CancelToken.source();

		const fetchOwners = () => {
			setData({owners: [], total_count: 0, isFetching: true});
			// Fetch People data.
			axios.get('/people/api/pre-reg-person/?light=true&page=' + page + '&search=' + searchTerm + '&owner_type=' + ownerType + '&organization=' + organization + '&hidden=' + showHidden, {
				cancelToken: source.token,
			})
			.then(response => {
				if (!unmounted) {
					setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
					setData({owners: response.data.results, total_count:response.data.count, isFetching: false});
				}
			})
			.catch(error => {
				if (!unmounted) {
					setData({owners: [], isFetching: false});
          setShowSystemError(true);
				}
			});
		};
    fetchOwners();

		// Cleanup.
		return () => {
			unmounted = true;
			source.cancel();
		};
	}, [searchTerm, page, ownerType, showHidden]);

	return (
    <div className="ms-2 me-2">
    <Header>
      <span id="back" style={{cursor:'pointer'}} onClick={(e) => {navigate("/" + organization + "/manage/organization")}} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
      Manage Preregistered Owners
    </Header>
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="ID #"
              name="searchIDTerm"
              onChange={handleIDChange}
            />
             <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/manage/owners/" + goToID)}}>Go</Button>
          </InputGroup>
        </Col>
        <Col>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Search"
              name="searchTerm"
              onChange={handleChange}
              ref={tempSearchTerm}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              />
              <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", color:"white"}}>Search
                <OverlayTrigger
                  key={"search-information"}
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-search-information`}>
                      Searchable fields: name, phone number, email, drivers license, address fields, evacuation zone, and animal names.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
                </OverlayTrigger>
              </Button>
              <ButtonGroup className="ms-1 border rounded" style={{height:"38px"}}>
                <Button
                  variant={showHidden === false ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setShowHidden(false);
                  }}
                >
                  Discoverable
                </Button>
                <Button
                  variant={showHidden === true ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setShowHidden(true);
                  }}
                >
                  Hidden
                </Button>
              </ButtonGroup>
              <ButtonGroup className="ms-1 me-1 border rounded" style={{height:"38px"}}>
                <Button
                  variant={ownerType === "operational" ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setOwnerType('operational');
                  }}
                >
                  Operational
                </Button>
                <Button
                  variant={ownerType === "training" ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setOwnerType('training');
                  }}
                >
                  Training
                </Button>
              </ButtonGroup>
              <ActionsDropdown variant="outline-light" title={"Actions"} search={true}>
                <LoadingLink
                  href={"/" + organization + "/manage/owners/new"}
                  className="d-block"
                  isLoading={false}
                  label="Add a New Preregistered Owner"
                  >
                    <FontAwesomeIcon icon={faPlusSquare} className="fas" inverse />
                </LoadingLink>
                <LoadingLink onClick={handleGeoJsonDownload} isLoading={data.isFetching} label="Preregistered Owners as GeoJSON" className="d-block">
                  <FontAwesomeIcon icon={faDownload} inverse />
                </LoadingLink>
              </ActionsDropdown>
          </InputGroup>
        </Col>
      </Row>
    </Form>
    <Row className="flex-nowrap mt-3">
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"0px"}}>
          Last Name
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
          First Name
      </Col>
      <Col style={{minWidth:"350px", maxWidth:"350px"}}>
          Address
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px"}}>
          Phone
      </Col>
      <Col style={{minWidth:"330px", maxWidth:"330px"}}>
          Email
      </Col>
      <Col style={{minWidth:"105px", maxWidth:"105px"}} className="pe-0">
        Animal Count
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
          Actions
      </Col>
    </Row>
    {data.owners.map(owner => (
      <Card key={owner.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"741px"}}>
        <div className="row no-gutters flex-nowrap me-0">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.last_name}>
            {owner.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.first_name}>
            {owner.first_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"350px", maxWidth:"350px", backgroundColor:"#615e5e"}} title={owner.full_address}>
            {owner.full_address}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.display_phone}>
            {owner.display_phone}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"330px", maxWidth:"330px", backgroundColor:"#615e5e"}} title={owner.email}>
            {owner.email ?? 'N/A'}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"105px", maxWidth:"105px", backgroundColor:"#615e5e"}}>
            {owner.pre_reg_animal_count}
          </Col>
          <Col className="border" style={{height:"32px", paddingRight:"3px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
						<ActionsDropdown variant="secondary" title={"Actions"} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
							<LoadingLink
								href={"/" + organization + "/manage/owners/" + owner.id}
								className="d-block"
								isLoading={false}
								label="Owner Details"
								>
								  <FontAwesomeIcon icon={faUserAlt} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
                  href={"/" + organization + "/manage/owners/edit/" + owner.id}
                  isLoading={false}
                  className="d-block"
                  label="Update Owner"
                >
                  <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                </LoadingLink>
                {owner.hidden ?
                  <LoadingLink
                    onClick={() => showHideOwner(owner.id, false)}
                    className="d-block"
                    isLoading={false}
                    label="Make Owner Discoverable"
                    >
                    <FontAwesomeIcon icon={faEye} className="fas" inverse />
                  </LoadingLink>
                  :
                  <LoadingLink
                    onClick={() => showHideOwner(owner.id, true)}
                    className="d-block"
                    isLoading={false}
                    label="Hide Owner From Discovery"
                    >
                    <FontAwesomeIcon icon={faEyeSlash} className="fas" inverse />
                  </LoadingLink>
                  }
						</ActionsDropdown>
          </Col>
        </div>
      </Card>
    ))}
    {/* {data.owners.map((owner, index) => (
      <div key={owner.id} className="mt-3 border rounded">
        <div className="card-header"> {owner.first_name ?
          <h4 style={{marginBottom: "-2px",  marginLeft:"-12px"}}>
            <OverlayTrigger
              key={"owner-details"}
              placement="top"
              overlay={
                <Tooltip id={`tooltip-owner-details`}>
                  Owner details
                </Tooltip>
              }
            >
              <Link href={"/" + organization + "/manage/owners/" + owner.id}><FontAwesomeIcon icon={faDotCircle} className="me-2" inverse/></Link>
            </OverlayTrigger>
            {owner.first_name} {owner.last_name}
          </h4> : "Unknown Name"}
        </div>
        <CardGroup style={{marginBottom:"-6px"}}>
          <Card style={{marginBottom:"6px"}}>
            <Card.Body>
              <Card.Title style={{marginTop:"-9px", marginBottom:"8px"}}>Information</Card.Title>
              <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                <ListGroup>
                  <ListGroup.Item><b>Phone: </b>{owner.phone ? <span>{owner.display_phone} </span> : "None"}</ListGroup.Item>
                  <ListGroup.Item><b>Email: </b>{owner.email ? <span>{owner.email} </span> : "None"}</ListGroup.Item>
                  {owner.full_address ?
                    <ListGroup.Item><b>Address: </b>{owner.full_address}</ListGroup.Item>
                  : ""}
                  <ListGroup.Item><b>Evac Zone: </b>{owner.evac_zone ? <span>{owner.evac_zone} </span> : "None"}</ListGroup.Item>
                </ListGroup>
              </Scrollbar>
            </Card.Body>
          </Card>
          {searchState[owner.id] ?
          <Card style={{marginBottom:"6px"}}>
            <Card.Body style={{width:"525px"}}>
              <Card.Title style={{marginTop:"-10px", marginBottom:"12px"}}>
                <Scrollbar horizontal="true" autoHide style={{height:"32px", width:"485px"}} renderView={props => <div {...props} style={{...props.style, marginBottom:"-18px", marginRight:"0px", overflowX:"auto", overflowY: "hidden"}}/>} renderThumbVertical={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  <ListGroup horizontal className="ps-0 pe-0 d-flex" style={{display:"inline-block", whiteSpace:"nowrap"}}>
                  {searchState[owner.id].species.map(species => (
                    <ListGroup.Item key={species} active={searchState[owner.id].selectedSpecies === species ? true : false} style={{textTransform:"capitalize", cursor:'pointer', paddingTop:"4px", paddingBottom:"4px"}} onClick={() => setSearchState(prevState => ({ ...prevState, [owner.id]:{...prevState[owner.id], selectedSpecies:species} }))}>{species}</ListGroup.Item>
                  ))}
                  </ListGroup>
                </Scrollbar>
              </Card.Title>
              <ListGroup style={{height:"159px", overflowY:"auto", marginTop:"-12px"}}>
                <Scrollbar style={{height:"159px", marginBottom:"-16px"}} renderThumbHorizontal={props => <div {...props} style={{...props.style, display: 'none'}} />}>
                  {owner.pre_reg_animals.filter(animal => animal.species_string_singular === searchState[owner.id].selectedSpecies).map((animal, i) => (
                    <ListGroup.Item key={animal.id} className="rounded">
                      <b><Link href={"/" + organization + "/manage/animals/" + animal.id} className="text-link" style={{textDecoration:"none", color:"white"}}>#{animal.id}</Link>:</b>&nbsp;&nbsp;{Number(animal.animal_count) === 1 ? <span>{animal.name || "Unknown"}</span> : <span>{animal.animal_count} {titleCase(animal.species_string_singular)}</span>}
                    </ListGroup.Item>
                  ))}
                  {owner.pre_reg_animals.length < 1 ? <ListGroup.Item>No Animals</ListGroup.Item> : ""}
                </Scrollbar>
              </ListGroup>
            </Card.Body>
          </Card>
          : ""}
        </CardGroup>
      </div>
    ))} */}
    <p className="mt-3">{data.isFetching ? 'Fetching Owners ...' :
      <span>{data.owners && data.owners.length ? '' : 'No Owners found.'}</span>}
    </p>
    <Pagination className="custom-page-links" size="lg" onClick={(e) => {setFocus(parseInt(e.target.innerText));setPage(parseInt(e.target.innerText))}}>
      {[...Array(numPages).keys()].map(x =>
      <Pagination.Item key={x+1} active={x+1 === page}>
        {x+1}
      </Pagination.Item>)
      }
    </Pagination>
	</div>
	)
}

export default PreRegOwnerSearch;
